/**
 * UnifiedInboxPage (`/dashboard/eingaenge`) — M-UX-5.2, one list for all three
 * Eingangs-Ströme (`architecture-ux-restructure.md` §6.5).
 *
 * Merges `received_invoices` (Datei-Upload) / `inbound_documents` (E-Mail) /
 * `scanned_receipts` (Foto/Scan) via the W5.1 data layer
 * (`utils/unifiedInbox/loadUnifiedInbox.ts`) into ONE `DataTable` with a
 * Kanal-Badge, a kanalunabhängigen Status-Badge and einheitliche Aktionen —
 * while the Erfassungs-Ströme themselves render above the list (owner-only,
 * hidden entirely in the Mandanten-Modus, §6.5). §15-Redesign (M-C,
 * architecture-ux-density-v2.md): the "So erhalten Sie Rechnungen" E-Mail-
 * Setup-Guide moved into a `Collapsible` (default zu, ganz oben, reusing
 * `InboundSetupGuide`/`FairUseMeter` unchanged) and the Upload/Scan cards
 * became full-width ROWS (`data-testid="unified-inbox-streams"`) instead of
 * the old 3-column Grid.
 *
 * "Als Ausgabe verbuchen" always re-derives the `ExpenseLinkIndex` from a
 * fresh `getExpensesForUser()` read (via `refresh()` after every successful
 * booking) rather than a locally-tracked Set — this is BOTH the "kein Reload
 * nötig" requirement (the list updates immediately after the callback) AND
 * the reload-fest Bestandsbug-Fix (`InboxPage`'s old session-local
 * `expensedIds` Set forgot already-booked docs after a reload; here the
 * booked state is Firestore truth every time, §6.1).
 *
 * @module UnifiedInboxPage
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { IconMailOpen, IconInbox, IconRefresh, IconClose } from '../icons';

import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';

import { loadUnifiedInbox } from '../utils/unifiedInbox/loadUnifiedInbox';
import type { LoadUnifiedInboxOptions } from '../utils/unifiedInbox/loadUnifiedInbox';
import { mergeAndSort } from '../utils/unifiedInbox/mappers';
import type { UnifiedInboxItem } from '../utils/unifiedInbox/types';

import { canUseInbound, canUseInboundTaxDecision } from '../config/inboundAccess';
import { canUseInboundDatev } from '../config/inboundDatevAccess';
import {
  canUseReceiptScan,
  RECEIPT_SCAN_FLAG,
  hasReceiptScanAvvConsent,
} from '../config/receiptScanAccess';
import ReceiptScanConsentGate from '../components/expenses/ReceiptScanConsentGate';
import { resolveTier } from '../config/entitlements';
import { isLoopBBeta } from '../config/loopBBetaAccess';
import { canUseDocChecks } from '../config/kanzleiAccess';
import type { SkrKontenrahmen } from '../utils/datevMapping';

import type { ReceivedInvoice, ParsedZugferdData } from '../types/receivedInvoiceTypes';
import type { InboundDocument } from '../types/inboundTypes';
import type { ScannedReceipt } from '../types/scannedReceiptTypes';

import type { ReceivedInvoiceUploadMeta } from '../components/receivedInvoices/ReceivedInvoiceUpload';
import ReceivedInvoiceDetail from '../components/receivedInvoices/ReceivedInvoiceDetail';
import InboundDocumentDetail from '../components/inbound/InboundDocumentDetail';
import InboundDatevExportSection from '../components/inbound/InboundDatevExportSection';
import ExpenseBookingModal from '../components/expenses/ExpenseBookingModal';
import ReceiptScanModal from '../components/expenses/ReceiptScanModal';
import { inboundToParsedZugferd } from '../utils/inboundAdapters';

import { deleteExpense } from '../api/expensesApi';
import { markScannedReceiptDiscarded } from '../api/scannedReceiptsApi';
import { markReceivedInvoiceDiscarded } from '../api/receivedInvoicesApi';

import Button from '../components/common/Button';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import Collapsible from '../components/common/Collapsible';
import InboundSetupGuide from '../components/inbound/InboundSetupGuide';
import FairUseMeter from '../components/inbound/FairUseMeter';
import CaptureBar from '../components/inbox/CaptureBar';
import UnifiedInboxFilterChips from '../components/inbox/UnifiedInboxFilterChips';
import UnifiedInboxTable from '../components/inbox/UnifiedInboxTable';
import {
  DEFAULT_FILTER_STATE,
  channelFilterFromStreamParam,
  filterUnifiedInboxItems,
  UnifiedInboxFilterState,
} from '../components/inbox/inboxFilters';

// ── Feature flags (fail-closed: default false unless explicitly 'true') ───────
const EXPENSES_ENABLED = process.env.REACT_APP_EXPENSES_ENABLED === 'true';

const UnifiedInboxPage: React.FC = () => {
  const { t } = useTranslation('posteingang');
  const { currentUser, isPremium, effectiveScopeUid, sellerData } = useAuth();
  const isReadOnly = useIsReadOnly();
  // Pricing-Repackaging §3.2: effektiver Owner-Tier für die Ultra-Gates
  // (Scanner-OR-Zweig + Inbound-DATEV). Im Advisor-Kontext (`isReadOnly`) trägt
  // die DATEV-Kette KEINEN Owner-Tier-Term (§3.2b).
  const tier = resolveTier(sellerData);
  const [searchParams] = useSearchParams();

  const sellerDatevConfig = useMemo(
    () => ({
      skr: (sellerData?.datevSkr as SkrKontenrahmen | undefined) ?? 'SKR03',
      beraterNr: sellerData?.datevBeraterNr ?? null,
      mandantenNr: sellerData?.datevMandantenNr ?? null,
    }),
    [sellerData?.datevSkr, sellerData?.datevBeraterNr, sellerData?.datevMandantenNr],
  );

  // ── List state ────────────────────────────────────────────────────────────
  const [items, setItems] = useState<UnifiedInboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [inboundCursor, setInboundCursor] = useState<LoadUnifiedInboxOptions['inboundCursor']>(undefined);
  const [hasMoreInbound, setHasMoreInbound] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // §8 Deep-Link-Erhalt: `?stream=upload|email|scan` (Redirect von den Alt-
  // Routen) vor-selektiert den Kanal-Filter. Nur beim ersten Render gelesen —
  // der Filter bleibt danach ausschließlich User-gesteuert.
  const [filter, setFilter] = useState<UnifiedInboxFilterState>(() => ({
    ...DEFAULT_FILTER_STATE,
    channel: channelFilterFromStreamParam(searchParams.get('stream')),
  }));

  // ── Detail-view state ────────────────────────────────────────────────────
  const [selectedReceived, setSelectedReceived] = useState<ReceivedInvoice | null>(null);
  const [selectedInbound, setSelectedInbound] = useState<InboundDocument | null>(null);

  // ── Booking-modal state (shared "Öffnen"/"Verbuchen" target for scan) ─────
  const [bookingReceived, setBookingReceived] = useState<ReceivedInvoice | null>(null);
  const [bookingInbound, setBookingInbound] = useState<InboundDocument | null>(null);
  const [bookingScan, setBookingScan] = useState<ScannedReceipt | null>(null);

  // ── Discard-confirmation state (Soft-Discard, M-V4-D2 §4.3) ────────────────
  const [discardTarget, setDiscardTarget] = useState<UnifiedInboxItem | null>(null);

  // M13a-GA: AVV-Zustimmungs-Dialog für den KI-Belegscanner (Foto-Fläche).
  const [showScanAvvDialog, setShowScanAvvDialog] = useState(false);
  const scanConsented = hasReceiptScanAvvConsent(sellerData?.receiptScanAvvAcceptedVersion);

  // ── E-Mail-Strom-Karte: eigene Mailbox (Owner-only) ──────────────────────
  const [mailboxAddress, setMailboxAddress] = useState<string | null>(null);
  const [monthCount, setMonthCount] = useState<number>(0);
  // Bug-Fix (2026-07-27): expliziter Status statt Dauer-„lädt", wenn noch
  // KEINE Mailbox existiert (Doc fehlt, weil AVV nie bestätigt wurde) oder der
  // Read fehlschlägt — vorher blieb `mailboxAddress===null` in BEIDEN Fällen
  // ununterscheidbar von "lädt noch" (Endlos-Spinner-Text im Collapsible).
  const [mailboxStatus, setMailboxStatus] = useState<'loading' | 'none' | 'ready' | 'error'>('loading');

  const canBookExpense = EXPENSES_ENABLED && isPremium && !isReadOnly;

  // ── Load / refresh the full merged list ──────────────────────────────────
  const refresh = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const result = await loadUnifiedInbox(effectiveScopeUid);
      setItems(result.items);
      setHasMoreInbound(result.hasMoreInbound);
      setInboundCursor(result.inboundCursor ?? undefined);
    } catch (err) {
      console.error('UnifiedInboxPage: failed to load unified inbox', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [currentUser, effectiveScopeUid]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ── Load own mailbox (address + fair-use) — owner context, entitled only ──
  // Umgebaut in ein wiederverwendbares `loadMailbox` (statt Effect-Body direkt),
  // damit der Retry-Button im 'error'-Zweig denselben Load erneut auslösen kann.
  // Re-Loop 1 (Review-MINOR 🟡-1): das ursprünglich hier eingesetzte
  // `mailboxUnmountedRef`-Bool war NICHT äquivalent zum ersetzten
  // `cancelled`-Closure-Muster — der Effect-Body setzte die Ref bei jedem
  // Dependency-Wechsel wieder auf `false` zurück und hob damit die Stornierung
  // des ÜBERHOLTEN Laufs auf: ein noch laufender `getDoc` eines alten Laufs
  // konnte danach seinen veralteten Wert schreiben. Fix: ein Generations-
  // Zähler (`mailboxRunRef`), der bei jedem Lauf hochgezählt und bei jedem
  // Cleanup (Unmount UND Dependency-Wechsel) erneut hochgezählt wird — nur der
  // zuletzt gestartete Lauf erkennt sich noch als aktuell.
  const mailboxRunRef = useRef(0);
  const loadMailbox = useCallback(async () => {
    const runId = ++mailboxRunRef.current;
    if (!currentUser || isReadOnly || !canUseInbound(effectiveScopeUid)) {
      setMailboxStatus('none');
      return;
    }
    setMailboxStatus('loading');
    try {
      const snap = await getDoc(doc(db, 'inbound_mailboxes', currentUser.uid));
      if (mailboxRunRef.current !== runId) return;
      if (!snap.exists()) {
        setMailboxStatus('none');
        return;
      }
      const data = snap.data() as { address?: string; fairUse?: { monthCount?: number } };
      if (typeof data.address !== 'string') {
        setMailboxStatus('none');
        return;
      }
      setMailboxAddress(data.address);
      if (typeof data.fairUse?.monthCount === 'number') setMonthCount(data.fairUse.monthCount);
      setMailboxStatus('ready');
    } catch (err) {
      // Non-critical: the setup guide/meter just stays hidden — but the user
      // now sees an explicit error + retry instead of a permanent spinner text.
      console.error('UnifiedInboxPage: failed to load mailbox', err);
      if (mailboxRunRef.current === runId) setMailboxStatus('error');
    }
  }, [currentUser, isReadOnly, effectiveScopeUid]);

  useEffect(() => {
    void loadMailbox();
    return () => {
      // Invalidiert den gerade gestarteten Lauf (Unmount ODER
      // Dependency-Wechsel) — KEIN Reset auf 0/false, sonst hebt der neue
      // Effect-Body die Stornierung des überholten Laufs wieder auf.
      mailboxRunRef.current += 1;
    };
  }, [loadMailbox]);

  // ── "Mehr laden" (inbound-only continuation, §6.4) ────────────────────────
  const handleLoadMoreInbound = useCallback(async () => {
    if (!inboundCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await loadUnifiedInbox(effectiveScopeUid, { inboundCursor });
      setItems((prev) => mergeAndSort([prev, result.items]));
      setHasMoreInbound(result.hasMoreInbound);
      setInboundCursor(result.inboundCursor ?? undefined);
    } catch (err) {
      console.error('UnifiedInboxPage: failed to load more', err);
    } finally {
      setLoadingMore(false);
    }
  }, [inboundCursor, loadingMore, effectiveScopeUid]);

  const filteredItems = useMemo(() => filterUnifiedInboxItems(items, filter), [items, filter]);

  const inboundDocsForDatev = useMemo(
    () =>
      items
        .filter((i): i is UnifiedInboxItem & { raw: InboundDocument } => i.sourceCollection === 'inbound_documents')
        .map((i) => i.raw),
    [items],
  );

  // ── Row action dispatch ──────────────────────────────────────────────────
  const handleOpen = useCallback((item: UnifiedInboxItem) => {
    if (item.sourceCollection === 'received_invoices') {
      setSelectedReceived(item.raw as ReceivedInvoice);
    } else if (item.sourceCollection === 'inbound_documents') {
      setSelectedInbound(item.raw as InboundDocument);
    } else {
      // scanned_receipts: the only existing viewer IS the booking-confirmation
      // modal (resolveRowActions already gates this to bookable/owner-only rows).
      setBookingScan(item.raw as ScannedReceipt);
    }
  }, []);

  const handleBook = useCallback((item: UnifiedInboxItem) => {
    if (item.sourceCollection === 'received_invoices') {
      setBookingReceived(item.raw as ReceivedInvoice);
    } else if (item.sourceCollection === 'inbound_documents') {
      setBookingInbound(item.raw as InboundDocument);
    } else {
      setBookingScan(item.raw as ScannedReceipt);
    }
  }, []);

  const handleBooked = useCallback(() => {
    setBookingReceived(null);
    setBookingInbound(null);
    setBookingScan(null);
    void refresh();
  }, [refresh]);

  // ── Verwerfen (Soft-Discard, M-V4-D2 §4.3) ────────────────────────────────
  const handleDiscard = useCallback((item: UnifiedInboxItem) => {
    setDiscardTarget(item);
  }, []);

  /**
   * Executes the Soft-Discard for one row (§4.3.5). Never throws — all errors
   * degrade to a toast (Golden Rule: no unhandled promises / silent failures).
   */
  const performDiscard = useCallback(
    async (target: UnifiedInboxItem) => {
      try {
        // §4.3.5 — Reihenfolge bindend: erst den verknüpften Ausgaben-Entwurf
        // löschen (nur im 'draft'-Zustand erlaubt, firestore.rules), DANN die
        // Quelle verwerfen.
        if (target.status === 'draft' && target.linkedExpenseId) {
          await deleteExpense(target.linkedExpenseId);
        }

        if (target.sourceCollection === 'scanned_receipts') {
          await markScannedReceiptDiscarded(target.sourceId);
        } else if (target.sourceCollection === 'received_invoices') {
          await markReceivedInvoiceDiscarded(target.sourceId);
        } else {
          // inbound_documents (E-Mail) ist WORM/client-read-only — kein
          // Verwerfen in v4 (canDiscard === false, dieser Zweig ist
          // unerreichbar). Fail-closed: nichts tun, keinen Schreibpfad raten.
          return;
        }

        toast.success(t('unifiedInbox.discard.success', 'Eingang verworfen.'));
      } catch (err) {
        console.error('UnifiedInboxPage: failed to discard inbox item', err);
        toast.error(t('unifiedInbox.discard.error', 'Der Eingang konnte nicht verworfen werden.'));
      } finally {
        // Erfolg → refresh zeigt die neue Klassifikation ('discarded'/'booked');
        // Teil-Fehler nach gelöschtem Entwurf (Edge #9) → refresh zeigt die
        // Wahrheit ('new') statt eines veralteten 'draft'.
        void refresh();
      }
    },
    [t, refresh],
  );

  const handleConfirmDiscard = useCallback(() => {
    const target = discardTarget;
    if (!target) return;
    // Dialog sofort schließen (verhindert Doppel-Submit); die Aktion läuft danach.
    setDiscardTarget(null);
    void performDiscard(target);
  }, [discardTarget, performDiscard]);

  // ── Upload-Strom-Karte callbacks ─────────────────────────────────────────
  const handleUploadSuccess = useCallback(
    async (data: ParsedZugferdData, meta: ReceivedInvoiceUploadMeta) => {
      if (!currentUser) return;
      const { filename, receivedInvoiceId, pdfStorageRef, byteSize } = meta;
      const uploadedAt = new Date().toISOString();
      try {
        const docPayload: Record<string, unknown> = {
          userId: effectiveScopeUid,
          uploadedAt,
          filename,
          data,
        };
        if (pdfStorageRef) {
          docPayload.pdfStorageRef = pdfStorageRef;
          docPayload.pdfUploadedAt = uploadedAt;
          if (byteSize !== null) docPayload.pdfByteSize = byteSize;
        }
        await setDoc(doc(db, 'received_invoices', receivedInvoiceId), docPayload);
        toast.success(t('unifiedInbox.upload.savedSuccess', 'Beleg gespeichert'));
        void refresh();
      } catch (err) {
        console.error('UnifiedInboxPage: failed to save received invoice', err);
        toast.error(t('unifiedInbox.upload.savedError', 'Fehler beim Speichern'));
      }
    },
    [currentUser, effectiveScopeUid, t, refresh],
  );

  const handleInboundUploadSuccess = useCallback(
    (result: { inboundDocumentId: string; deferred: boolean }) => {
      toast.success(
        result.deferred
          ? t('unifiedInbox.upload.inboundDeferred', 'Sicher archiviert — die Auswertung folgt.')
          : t('unifiedInbox.upload.inboundArchived', 'Archiviert — erscheint gleich in der Liste unten.'),
      );
      void refresh();
    },
    [t, refresh],
  );

  const handleCaptured = useCallback(() => {
    void refresh();
  }, [refresh]);

  // ── Selected-inbound "already linked" lookup (mirrors InboxPage's expensedIds gate) ──
  const selectedInboundStatus = useMemo(() => {
    if (!selectedInbound) return undefined;
    return items.find(
      (i) => i.sourceCollection === 'inbound_documents' && i.sourceId === selectedInbound.id,
    )?.status;
  }, [items, selectedInbound]);

  // ── Selected-received "already linked" lookup (F4, mirrors selectedInboundStatus
  //    above — architecture-goal-durchstich-fixplan.md §1 A1: UnifiedInboxPage has
  //    its own booking mechanic via `bookingReceived`/`handleBook`, not
  //    `inboxRowActions`; this reuses the SAME `ExpenseLinkIndex`-derived `items`
  //    status the list view already relies on). ─────────────────────────────────
  const selectedReceivedStatus = useMemo(() => {
    if (!selectedReceived) return undefined;
    return items.find(
      (i) => i.sourceCollection === 'received_invoices' && i.sourceId === selectedReceived.id,
    )?.status;
  }, [items, selectedReceived]);

  // ── Manuelle Steuerkontierung (owner-only v1, §5.5): gated auf den effektiven
  //    Tenant-Scope UND currentUser==Scope (Owner). Advisor-Scope-Ansicht bleibt
  //    read-only (kein Schreibpfad, J-3). ──────────────────────────────────────
  const canDecideTax =
    canUseInboundTaxDecision(effectiveScopeUid) &&
    !!currentUser?.uid &&
    currentUser.uid === effectiveScopeUid;

  // Nach einer Kontierungs-Entscheidung: neuen Summary-Stand in items + Detail
  // mergen (kein Full-Reload). Anti-Pattern-Warnung (§12): NICHT nur den
  // InboxPage-Pfad verdrahten — beide Inbox-Seiten müssen mergen.
  const handleTaxDecided = useCallback(
    (docId: string, taxDecision: InboundDocument['taxDecision']) => {
      setItems((prev) =>
        prev.map((it) =>
          it.sourceCollection === 'inbound_documents' && it.sourceId === docId
            ? { ...it, raw: { ...(it.raw as InboundDocument), taxDecision } }
            : it,
        ),
      );
      setSelectedInbound((prev) =>
        prev && prev.id === docId ? { ...prev, taxDecision } : prev,
      );
    },
    [],
  );

  // ── Detail views (full-width, replace the list) ──────────────────────────
  if (selectedReceived) {
    return (
      <div className="min-h-screen bg-surface p-4 md:p-6">
        <div className="max-w-5xl mx-auto">
          <ReceivedInvoiceDetail
            invoice={selectedReceived}
            onBack={() => setSelectedReceived(null)}
            // F4 (architecture-goal-durchstich-fixplan.md §1 A1): same
            // canBookExpense/selectedInboundStatus pattern as the inbound branch
            // below — the modal target is the existing `bookingReceived` state
            // (already wired to the list-row action via `handleBook`).
            canBookExpense={canBookExpense && selectedReceivedStatus === 'new'}
            onBookExpense={(inv) => setBookingReceived(inv)}
            // F4-reloop: `selectedReceivedStatus` is 'draft'/'booked' once an
            // expense is linked (mirrors expensedInvoiceIds.has(...) in
            // ReceivedInvoicesPage) — same "bereits übernommen" hint.
            alreadyExpensed={selectedReceivedStatus === 'draft' || selectedReceivedStatus === 'booked'}
          />
        </div>
        {/* F4: this branch `return`s before the list-view's bottom modal mounts
            (same reason the inbound branch below mounts its own), so the
            booking modal needs its own instance here too. */}
        {bookingReceived && (
          <ExpenseBookingModal
            open={true}
            invoice={bookingReceived}
            onClose={() => setBookingReceived(null)}
            onBooked={handleBooked}
          />
        )}
      </div>
    );
  }

  if (selectedInbound) {
    return (
      <div className="min-h-screen bg-surface p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <InboundDocumentDetail
            doc={selectedInbound}
            onBack={() => setSelectedInbound(null)}
            canBookExpense={canBookExpense && selectedInboundStatus === 'new'}
            onBookExpense={(d) => setBookingInbound(d)}
            // M-GA-4: Verified seal = Cluster D (docChecks); VAT-check = Cluster H
            // (stays isLoopBBeta, deliberately not decoupled).
            showVerifiedSeal={canUseDocChecks(currentUser?.uid, sellerData)}
            showVatCheck={isLoopBBeta(currentUser?.uid, sellerData)}
            // Manuelle Steuerkontierung (§5.4/§6): owner-only v1, gated.
            canDecideTax={canDecideTax}
            onTaxDecided={handleTaxDecided}
          />
        </div>
        {bookingInbound && (
          <ExpenseBookingModal
            open={true}
            invoice={inboundToParsedZugferd(bookingInbound)}
            onClose={() => setBookingInbound(null)}
            onBooked={handleBooked}
          />
        )}
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <>
      <div className="mx-auto max-w-screen-2xl">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
                <IconInbox className="text-accent-ink" size={24} />
                {t('unifiedInbox.title', 'Eingänge')}
              </h1>
              <p className="text-muted text-sm mt-1">
                {t(
                  'unifiedInbox.subtitle',
                  'Alle eingehenden Belege — hochgeladen, per E-Mail empfangen oder fotografiert — an einem Ort.',
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void refresh()}
              disabled={loading}
              title={t('refresh', 'Aktualisieren')}
              aria-label={t('refresh', 'Aktualisieren')}
              data-testid="unified-inbox-refresh"
              className="shrink-0 flex items-center gap-2 text-sm bg-surface-raised hover:bg-surface-inset border border-hairline disabled:opacity-60 text-secondary px-4 py-2 rounded-xl font-medium transition-colors"
            >
              <IconRefresh className={loading ? 'animate-spin' : ''} size={14} />
              {t('refresh', 'Aktualisieren')}
            </button>
          </div>

          {/* §15-Redesign: Collapsible "So erhalten Sie Rechnungen" (default zu, ganz
              oben) + Erfassungs-REIHEN (volle Breite, statt der alten 3-Säulen-Grid) —
              beide Owner-only, im Mandanten-Modus komplett ausgeblendet (§6.5/§15). */}
          {!isReadOnly && (
            <>
              <Collapsible
                title={t('unifiedInbox.setupCollapsible.title', 'So erhalten Sie Rechnungen')}
                icon={<IconMailOpen size={14} />}
                defaultOpen={false}
                data-testid="unified-inbox-setup-collapsible"
              >
                {canUseInbound(effectiveScopeUid) ? (
                  <>
                    {mailboxStatus === 'loading' && (
                      <p className="text-muted text-sm" data-testid="unified-inbox-setup-loading">
                        {t('loading', 'Wird geladen …')}
                      </p>
                    )}
                    {mailboxStatus === 'ready' && mailboxAddress && (
                      <div className="space-y-3">
                        <InboundSetupGuide address={mailboxAddress} />
                        <div className="bg-surface-inset border border-default rounded-xl px-4 py-3">
                          <FairUseMeter monthCount={monthCount} isPremium={isPremium} />
                        </div>
                      </div>
                    )}
                    {mailboxStatus === 'none' && (
                      <div className="space-y-2" data-testid="unified-inbox-setup-not-activated">
                        <p className="text-sm font-medium text-primary">
                          {t(
                            'unifiedInbox.setupCollapsible.notActivatedTitle',
                            'Empfangsadresse noch nicht aktiviert',
                          )}
                        </p>
                        <p className="text-sm text-muted">
                          {t(
                            'unifiedInbox.setupCollapsible.notActivatedBody',
                            'Um Rechnungen per E-Mail zu empfangen, richten Sie einmalig Ihre eigene Empfangsadresse ein. Dabei bestätigen Sie den Auftragsverarbeitungsvertrag (AVV) für die Verarbeitung Ihrer Eingangsrechnungen.',
                          )}
                        </p>
                        <Link
                          to="/dashboard/konto#empfangspostfach"
                          className="inline-block text-sm font-medium text-accent-ink hover:underline"
                          data-testid="unified-inbox-setup-activate-link"
                        >
                          {t('unifiedInbox.setupCollapsible.notActivatedCta', 'Empfangsadresse einrichten')}
                        </Link>
                      </div>
                    )}
                    {mailboxStatus === 'error' && (
                      <div className="space-y-2" role="status" data-testid="unified-inbox-setup-error">
                        <p className="text-sm text-muted">
                          {t(
                            'unifiedInbox.setupCollapsible.statusError',
                            'Der Status Ihrer Empfangsadresse konnte nicht geladen werden.',
                          )}
                        </p>
                        <Button variant="secondary" size="sm" onClick={() => void loadMailbox()}>
                          {t('unifiedInbox.retry', 'Erneut versuchen')}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  // E-Mail-Nicht-Entitled: schmale Info-Zeile IM Collapsible statt
                  // einer eigenen Säulen-Karte (§15) — Bestand-Wortlaut unverändert.
                  <p className="text-muted text-sm">
                    {t('unifiedInbox.setupCollapsible.notEntitledInfo', 'Der E-Mail-Empfang ist bald für Sie verfügbar.')}
                  </p>
                )}
              </Collapsible>

              {/* M-V3-C (§5): the two oversized Erfassungs-Karten collapsed into
                  ONE thin CaptureBar. Gates stay at the page level (§5.6). */}
              <CaptureBar
                onUploadSuccess={handleUploadSuccess}
                onInboundUploadSuccess={handleInboundUploadSuccess}
                onCaptured={handleCaptured}
                scanEntitled={canUseReceiptScan(currentUser?.uid, tier)}
                scanFlagOn={RECEIPT_SCAN_FLAG}
                scanConsentPending={!scanConsented}
                onScanRequiresConsent={() => setShowScanAvvDialog(true)}
              />
            </>
          )}

          {/* Filter-Chips + die EINE Liste */}
          <div className="space-y-4">
            <UnifiedInboxFilterChips filter={filter} onChange={setFilter} />

            {loading && (
              <div className="flex justify-center py-16 text-muted text-sm">{t('loading', 'Wird geladen …')}</div>
            )}

            {!loading && loadError && (
              <div className="bg-surface-raised border border-[var(--tone-danger-border)] rounded-xl p-6 text-center">
                <p className="text-[var(--tone-danger-fg)] text-sm mb-3">
                  {t('unifiedInbox.loadError', 'Die Liste konnte nicht geladen werden.')}
                </p>
                <Button variant="secondary" size="sm" onClick={() => void refresh()}>
                  {t('unifiedInbox.retry', 'Erneut versuchen')}
                </Button>
              </div>
            )}

            {!loading && !loadError && (
              <UnifiedInboxTable
                items={filteredItems}
                canBookExpense={canBookExpense}
                isReadOnly={isReadOnly}
                onOpen={handleOpen}
                onBook={handleBook}
                onDiscard={handleDiscard}
              />
            )}

            {!loading && !loadError && hasMoreInbound && (
              <div className="flex justify-center">
                <Button variant="secondary" size="sm" loading={loadingMore} onClick={() => void handleLoadMoreInbound()}>
                  {t('loadMore', 'Mehr laden')}
                </Button>
              </div>
            )}
          </div>

          {/* IDV-6: Eingangsbelege-DATEV-Belegtransfer — bewusst OHNE `!isReadOnly`
              (architecture-inbound-datev.md §5.5: "Owner ODER aktiver StB"). */}
          {canUseInboundDatev(effectiveScopeUid, tier, isReadOnly) && currentUser && (
            <InboundDatevExportSection docs={inboundDocsForDatev} sellerDatevConfig={sellerDatevConfig} />
          )}
        </div>
      </div>

      {/* Booking modals — mounted only while a target is selected. */}
      {bookingReceived && (
        <ExpenseBookingModal
          open={true}
          invoice={bookingReceived}
          onClose={() => setBookingReceived(null)}
          onBooked={handleBooked}
        />
      )}
      {bookingInbound && (
        <ExpenseBookingModal
          open={true}
          invoice={inboundToParsedZugferd(bookingInbound)}
          onClose={() => setBookingInbound(null)}
          onBooked={handleBooked}
        />
      )}
      {bookingScan && (
        <ReceiptScanModal open={true} receipt={bookingScan} onClose={() => setBookingScan(null)} onBooked={handleBooked} />
      )}

      {/* M13a-GA AVV-Zustimmungs-Dialog (Foto-Fläche). Overlay-Markup nach dem
          ImportWizard-Vorbild (fixed inset-0 + zentriertes Panel). Nach Zustimmung
          schließt der Dialog; der Nutzer tippt erneut auf „Foto" (bewusst kein
          Weiter-Reichen der gewählten Datei — hält den Flow trivial). */}
      {showScanAvvDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
          data-testid="unified-inbox-scan-avv-overlay"
        >
          <div className="bg-surface border border-hairline rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
              <span className="text-primary font-semibold">
                {t('unifiedInbox.scan.avvDialogTitle', 'KI-Belegscanner aktivieren')}
              </span>
              <button
                type="button"
                onClick={() => setShowScanAvvDialog(false)}
                className="text-muted hover:text-primary"
                aria-label={t('common:close', 'Schließen')}
                data-testid="unified-inbox-scan-avv-close"
              >
                <IconClose />
              </button>
            </div>
            <div className="p-6">
              <ReceiptScanConsentGate onConsented={() => setShowScanAvvDialog(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Verwerfen-Bestätigung (Soft-Discard, M-V4-D2 §4.3). Draft-Zeilen zeigen
          den Zwei-Schritt-Hinweis (Entwurf wird zusätzlich gelöscht, §4.3.5). */}
      {discardTarget && (
        <ConfirmationDialog
          isOpen={true}
          title={t('unifiedInbox.discard.confirmTitle', 'Eingang verwerfen?')}
          message={
            discardTarget.status === 'draft'
              ? t(
                  'unifiedInbox.discard.confirmBodyDraft',
                  'Der verknüpfte Ausgaben-Entwurf wird gelöscht und der Eintrag als „Verworfen“ markiert. Das Originaldokument bleibt archiviert.',
                )
              : t(
                  'unifiedInbox.discard.confirmBody',
                  'Der Eintrag wird als „Verworfen“ markiert und kann nicht mehr verbucht werden. Das Originaldokument bleibt archiviert.',
                )
          }
          onClose={() => setDiscardTarget(null)}
          options={[
            {
              label: t('unifiedInbox.actions.discard', 'Verwerfen'),
              action: handleConfirmDiscard,
              variant: 'danger',
            },
            {
              label: t('common:cancel', 'Abbrechen'),
              action: () => setDiscardTarget(null),
              variant: 'secondary',
            },
          ]}
        />
      )}
    </>
  );
};

export default UnifiedInboxPage;
